import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Link2, Plus, Key, Trash2, Copy, 
  Truck, Wrench, ClipboardCheck, Users, Activity, Coins 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { app_id: 'ridero', name: 'Ridero', description: 'Delivery dispatch - drivers, orders, deliveries', icon: Truck, color: '#3B82F6' },
  { app_id: 'mechanic_kiosk', name: 'MechanicKiosk', description: 'Touchscreen for ordering truck parts', icon: Wrench, color: '#10B981' },
  { app_id: 'pti_pector', name: 'PTI Pector', description: 'Truck inspection - locations, plates, issues', icon: ClipboardCheck, color: '#F97316' },
];

export default function AppsPage() {
  const [apiKeys, setApiKeys] = useState([]);
  const [appStats, setAppStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Create key dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState('');
  const [keyName, setKeyName] = useState('');
  const [newKey, setNewKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [keysRes, ...statsRes] = await Promise.all([
        axios.get(`${API}/api-keys`, { withCredentials: true }),
        ...APPS.map(app => 
          axios.get(`${API}/apps/${app.app_id}/stats`, { withCredentials: true })
            .then(res => ({ app_id: app.app_id, ...res.data }))
            .catch(() => ({ app_id: app.app_id, event_count: 0, user_count: 0, total_points_earned: 0 }))
        )
      ]);
      
      setApiKeys(keysRes.data);
      
      const statsMap = {};
      statsRes.forEach(stat => {
        statsMap[stat.app_id] = stat;
      });
      setAppStats(statsMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!selectedApp || !keyName.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const res = await axios.post(`${API}/api-keys`, {
        app_id: selectedApp,
        name: keyName
      }, { withCredentials: true });
      
      setNewKey(res.data.api_key);
      toast.success('API key created! Copy it now - it won\'t be shown again.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!confirm('Are you sure? This will invalidate the API key.')) return;
    
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { withCredentials: true });
      toast.success('API key deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedApp('');
    setKeyName('');
    setNewKey(null);
  };

  return (
    <DashboardLayout>
      <Header title="Connected Apps" subtitle="Manage SSO integrations and API keys" />
      
      <div className="p-6 space-y-6">
        {/* Apps Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {APPS.map((app) => {
            const Icon = app.icon;
            const stats = appStats[app.app_id] || {};
            
            return (
              <Card 
                key={app.app_id} 
                className="app-card card-hover"
                style={{ '--app-color': app.color }}
                data-testid={`app-overview-${app.app_id}`}
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-sm flex items-center justify-center"
                      style={{ backgroundColor: `${app.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: app.color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{app.name}</CardTitle>
                      <CardDescription className="text-xs">{app.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{stats.user_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Users</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.event_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Events</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.total_points_earned || 0}</p>
                      <p className="text-xs text-muted-foreground">Points</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Integration Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">Integration Guide</CardTitle>
            <CardDescription>How to connect your apps to Leader Hub</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="font-semibold">SSO Integration</h3>
                <p className="text-sm text-muted-foreground">
                  Use the SSO endpoint to validate user sessions from your connected apps.
                </p>
                <div className="bg-secondary p-3 rounded-sm font-mono text-xs overflow-x-auto">
                  <code>POST /api/sso/validate</code><br/>
                  <code>Header: X-API-Key: your_api_key</code><br/>
                  <code>Body: {`{ "session_token": "user_session" }`}</code>
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold">Event Webhooks</h3>
                <p className="text-sm text-muted-foreground">
                  Send events to automatically award points and sync activity.
                </p>
                <div className="bg-secondary p-3 rounded-sm font-mono text-xs overflow-x-auto">
                  <code>POST /api/events</code><br/>
                  <code>Header: X-API-Key: your_api_key</code><br/>
                  <code>Body: {`{ "app_id": "ridero", "event_type": "delivery_completed", "user_email": "..." }`}</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* API Keys */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-secondary uppercase">API Keys</CardTitle>
              <CardDescription>Manage API keys for connected apps</CardDescription>
            </div>
            <Button onClick={() => setDialogOpen(true)} data-testid="create-api-key-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Key
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>API Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No API keys created yet
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((key) => (
                    <TableRow key={key.key_id} className="table-row-hover">
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{key.app_id}</Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-secondary px-2 py-1 rounded">{key.api_key}</code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={key.is_active ? 'default' : 'secondary'}>
                          {key.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {key.last_used ? new Date(key.last_used).toLocaleString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteApiKey(key.key_id)}
                          data-testid={`delete-key-${key.key_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create Key Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>{newKey ? 'API Key Created' : 'Create API Key'}</DialogTitle>
            <DialogDescription>
              {newKey 
                ? 'Copy your new API key now. It won\'t be shown again!'
                : 'Generate a new API key for a connected app'
              }
            </DialogDescription>
          </DialogHeader>
          
          {newKey ? (
            <div className="py-4">
              <div className="flex gap-2">
                <Input value={newKey} readOnly className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(newKey)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-destructive mt-2">
                Make sure to copy this key. You won't be able to see it again!
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>App</Label>
                <Select value={selectedApp} onValueChange={setSelectedApp}>
                  <SelectTrigger data-testid="select-app">
                    <SelectValue placeholder="Select an app" />
                  </SelectTrigger>
                  <SelectContent>
                    {APPS.map((app) => (
                      <SelectItem key={app.app_id} value={app.app_id}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Key Name</Label>
                <Input
                  placeholder="e.g. Production Key"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  data-testid="key-name-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {newKey ? (
              <Button onClick={closeDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button onClick={createApiKey} data-testid="create-key-submit">Create Key</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
